import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  ChevronUp,
  ListMusic,
  Moon,
  Pause,
  Play,
  Radio,
  Repeat,
  Shuffle,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { usePlayer } from "../contexts/PlayerContext";
import { getRelatedTracks } from "../services/soundcloud/api";
import { mapTrackToCard } from "../services/soundcloud/mappers";

function PlayerBar() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandedMounted, setIsExpandedMounted] = useState(false);
  const [isExpandedClosing, setIsExpandedClosing] = useState(false);
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const [isRelatedOpen, setIsRelatedOpen] = useState(false);
  const [isSleepOpen, setIsSleepOpen] = useState(false);
  const [customSleepMinutes, setCustomSleepMinutes] = useState("25");
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [relatedTracks, setRelatedTracks] = useState([]);
  const [relatedTrackId, setRelatedTrackId] = useState(0);
  const closeTimerRef = useRef(null);

  const {
    queue,
    currentIndex,
    currentTrack,
    isPlaying,
    isBuffering,
    shuffleEnabled,
    repeatMode,
    volume,
    duration,
    currentTime,
    progressPercent,
    currentTimeLabel,
    durationLabel,
    togglePlayPause,
    playNext,
    playPrevious,
    playQueueIndex,
    playTrack,
    seekTo,
    setVolumeLevel,
    toggleMute,
    toggleShuffle,
    cycleRepeatMode,
    setSleepTimer,
    clearSleepTimer,
    sleepTimerEndsAt,
    sleepTimerRemainingMs,
  } = usePlayer();

  const song = currentTrack || {
    title: "Select a track",
    artist: "No playback yet",
    image:
      "https://images.unsplash.com/photo-1461784121038-f088ca1e7714?auto=format&fit=crop&w=400&q=80",
  };

  const repeatLabel = repeatMode === "one" ? "Repeat one" : repeatMode === "all" ? "Repeat all" : "Repeat off";

  const openExpandedPlayer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsExpandedMounted(true);
    setIsExpandedClosing(false);
    setIsExpanded(true);
  };

  const closeExpandedPlayer = () => {
    setIsExpanded(false);
    setIsExpandedClosing(true);

    closeTimerRef.current = window.setTimeout(() => {
      setIsExpandedMounted(false);
      setIsExpandedClosing(false);
      closeTimerRef.current = null;
    }, 280);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  function handleQueueClick() {
    if (!queue.length) {
      toast("Queue is empty", { icon: "🎶" });
      return;
    }
    setIsQueueOpen(true);
  }

  async function handleFetchRelated() {
    const trackId = Number(currentTrack?.id);
    if (!trackId) {
      toast("Play a track first", { icon: "🎵" });
      return;
    }

    if (isLoadingRelated) return;
    setIsLoadingRelated(true);

    try {
      if (relatedTrackId === trackId && relatedTracks.length) {
        setIsRelatedOpen(true);
        return;
      }

      const response = await getRelatedTracks(trackId, { limit: 20 });
      const collection = Array.isArray(response?.collection) ? response.collection : [];
      const mappedTracks = collection.map(mapTrackToCard);
      setRelatedTracks(mappedTracks);
      setRelatedTrackId(trackId);
      setIsRelatedOpen(true);

      if (!mappedTracks.length) {
        toast("No related tracks found", { icon: "ℹ️" });
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load related tracks.");
    } finally {
      setIsLoadingRelated(false);
    }
  }

  function renderControls(size = "default") {
    return (
      <div className={`player-controls${size === "expanded" ? " player-controls-expanded" : ""}`}>
        <button
          type="button"
          aria-label="Shuffle"
          className={shuffleEnabled ? "is-active" : ""}
          onClick={toggleShuffle}
        >
          <Shuffle size={size === "expanded" ? 19 : 18} />
        </button>
        <button type="button" aria-label="Previous" onClick={playPrevious}>
          <SkipBack size={size === "expanded" ? 22 : 20} />
        </button>
        <button
          type="button"
          className="play-main"
          aria-label={isPlaying ? "Pause" : "Play"}
          onClick={togglePlayPause}
        >
          {isPlaying ? <Pause size={size === "expanded" ? 20 : 18} /> : <Play size={size === "expanded" ? 20 : 18} />}
        </button>
        <button type="button" aria-label="Next" onClick={playNext}>
          <SkipForward size={size === "expanded" ? 22 : 20} />
        </button>
        <button
          type="button"
          aria-label={repeatLabel}
          className={repeatMode !== "off" ? "is-active" : ""}
          onClick={cycleRepeatMode}
          title={repeatLabel}
        >
          <Repeat size={size === "expanded" ? 19 : 18} />
          {repeatMode === "one" ? <span className="repeat-one-indicator">1</span> : null}
        </button>
      </div>
    );
  }

  function formatSleepRemaining(ms) {
    const safe = Math.max(0, Math.floor(Number(ms) || 0));
    const totalSeconds = Math.floor(safe / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remMinutes = minutes % 60;
      return `${hours}:${String(remMinutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  const sleepPresets = [10, 20, 30, 45, 60];

  function applyCustomSleepTimer() {
    const minutes = Number(customSleepMinutes);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      toast.error("Enter a valid number of minutes.");
      return;
    }

    if (minutes > 720) {
      toast.error("Maximum sleep timer is 720 minutes.");
      return;
    }

    setSleepTimer(minutes);
    setIsSleepOpen(false);
  }

  function renderProgress(extraClass = "") {
    return (
      <div className={`progress-area${extraClass}`}>
        <span>{currentTimeLabel}</span>
        <div className="progress-track" role="presentation">
          <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
          <input
            type="range"
            min={0}
            max={Math.max(duration, 1)}
            value={Math.min(currentTime, Math.max(duration, 1))}
            onChange={(event) => seekTo(Number(event.target.value))}
            aria-label="Seek"
            className="progress-input"
          />
        </div>
        <span>{duration > 0 ? durationLabel : "--:--"}</span>
      </div>
    );
  }

  return (
    <>
      <footer className={`player-bar glass-panel glow-player${isExpanded ? " is-expanded" : ""}`}>
        <div className="player-song">
          <img src={song.image} alt="Now playing cover" />
          <div>
            <p className="now-playing-title">{song.title}</p>
            <p className="now-playing-artist">{isBuffering ? "Buffering..." : song.artist}</p>
          </div>
          <button
            type="button"
            className="player-expand-toggle"
            aria-label="Expand player"
            onClick={openExpandedPlayer}
          >
            <ChevronUp size={18} />
          </button>
        </div>

        {renderControls()}

        {renderProgress()}

        <div className="player-extra">
          <button type="button" aria-label="Volume" onClick={toggleMute}>
            {volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <input
            className="volume-slider volume-slider-inline"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={(event) => setVolumeLevel(Number(event.target.value))}
            aria-label="Volume slider"
          />
          <button type="button" aria-label="Queue" onClick={handleQueueClick}>
            <ListMusic size={18} />
          </button>
          <button
            type="button"
            aria-label="Sleep timer"
            title={sleepTimerEndsAt ? `Sleep in ${formatSleepRemaining(sleepTimerRemainingMs)}` : "Sleep timer"}
            onClick={() => setIsSleepOpen(true)}
            className={sleepTimerEndsAt ? "is-active sleep-btn" : "sleep-btn"}
          >
            <Moon size={18} />
            {sleepTimerEndsAt ? (
              <span className="sleep-time-badge">{formatSleepRemaining(sleepTimerRemainingMs)}</span>
            ) : null}
          </button>
          <button
            type="button"
            aria-label="Fetch related tracks"
            onClick={handleFetchRelated}
            disabled={isLoadingRelated}
            className={isLoadingRelated ? "is-active" : ""}
          >
            <Radio size={18} />
          </button>
        </div>
      </footer>

      {isExpandedMounted ? (
        <div
          className={`player-expanded-overlay${
            isExpandedClosing ? " is-closing" : isExpanded ? " is-open" : ""
          }`}
          role="dialog"
          aria-modal="true"
          onClick={closeExpandedPlayer}
        >
          <div className="player-expanded glass-panel" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="player-expanded-close"
              aria-label="Close expanded player"
              onClick={closeExpandedPlayer}
            >
              <X size={18} />
            </button>

            <img className="player-expanded-art" src={song.image} alt={song.title} />
            <p className="player-expanded-title">{song.title}</p>
            <p className="player-expanded-artist">{song.artist}</p>

            {renderControls("expanded")}
            {renderProgress(" player-expanded-progress")}

            <div className="player-extra player-extra-expanded">
              <button type="button" aria-label="Volume" onClick={toggleMute}>
                {volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                className="volume-slider"
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={(event) => setVolumeLevel(Number(event.target.value))}
                aria-label="Volume slider"
              />
              <button type="button" aria-label="Queue" onClick={handleQueueClick}>
                <ListMusic size={18} />
              </button>
              <button
                type="button"
                aria-label="Sleep timer"
                title={sleepTimerEndsAt ? `Sleep in ${formatSleepRemaining(sleepTimerRemainingMs)}` : "Sleep timer"}
                onClick={() => setIsSleepOpen(true)}
                className={sleepTimerEndsAt ? "is-active sleep-btn" : "sleep-btn"}
              >
                <Moon size={18} />
                {sleepTimerEndsAt ? (
                  <span className="sleep-time-badge">{formatSleepRemaining(sleepTimerRemainingMs)}</span>
                ) : null}
              </button>
              <button
                type="button"
                aria-label="Fetch related tracks"
                onClick={handleFetchRelated}
                disabled={isLoadingRelated}
                className={isLoadingRelated ? "is-active" : ""}
              >
                <Radio size={18} />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isQueueOpen ? (
        <div className="queue-modal-overlay" onClick={() => setIsQueueOpen(false)}>
          <article
            className="queue-modal glass-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Queue"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="queue-modal-head">
              <h3>Now Playing Queue</h3>
              <button
                type="button"
                className="queue-modal-close"
                aria-label="Close queue"
                onClick={() => setIsQueueOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <p className="queue-modal-subtitle">
              {queue.length} track{queue.length === 1 ? "" : "s"} in queue
            </p>

            <div className="queue-list">
              {queue.map((item, index) => {
                const isCurrent = index === currentIndex;
                return (
                  <button
                    key={`${item.id}-${index}`}
                    type="button"
                    className={`queue-item${isCurrent ? " is-current" : ""}${item.isUnavailable ? " is-unavailable" : ""}`}
                    onClick={() => playQueueIndex(index)}
                    disabled={item.isUnavailable}
                  >
                    <img src={item.image} alt={item.title} />
                    <div className="queue-item-content">
                      <p className="queue-item-title">{item.title}</p>
                      <p className={`queue-item-artist${item.isUnavailable ? " is-warning" : ""}`}>
                        {item.isUnavailable
                          ? item.unavailableText || "Not available in your country"
                          : item.artist}
                      </p>
                    </div>
                    <span className="queue-item-time">
                      {item.isUnavailable
                        ? "Blocked"
                        : item.durationMs > 0
                          ? `${Math.floor(item.durationMs / 60000)}:${String(Math.floor((item.durationMs % 60000) / 1000)).padStart(2, "0")}`
                          : "--:--"}
                    </span>
                  </button>
                );
              })}
            </div>
          </article>
        </div>
      ) : null}

      {isSleepOpen ? (
        <div className="queue-modal-overlay" onClick={() => setIsSleepOpen(false)}>
          <article
            className="queue-modal sleep-modal glass-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Sleep timer"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="queue-modal-head">
              <h3>Sleep Timer</h3>
              <button
                type="button"
                className="queue-modal-close"
                aria-label="Close sleep timer"
                onClick={() => setIsSleepOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <p className="queue-modal-subtitle">
              {sleepTimerEndsAt
                ? `Playback will pause in ${formatSleepRemaining(sleepTimerRemainingMs)}`
                : "Choose when playback should stop"}
            </p>

            <div className="sleep-preset-grid">
              {sleepPresets.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  className="sleep-preset-btn"
                  onClick={() => {
                    setSleepTimer(minutes);
                    setIsSleepOpen(false);
                  }}
                >
                  {minutes} min
                </button>
              ))}
            </div>

            <div className="sleep-custom-row">
              <label htmlFor="custom-sleep-minutes">Custom (minutes)</label>
              <div className="sleep-custom-controls">
                <input
                  id="custom-sleep-minutes"
                  type="number"
                  min={1}
                  max={720}
                  step={1}
                  value={customSleepMinutes}
                  onChange={(event) => setCustomSleepMinutes(event.target.value)}
                />
                <button type="button" className="sleep-apply-btn" onClick={applyCustomSleepTimer}>
                  Set
                </button>
              </div>
            </div>

            <div className="sleep-actions">
              <button
                type="button"
                className="sleep-clear-btn"
                disabled={!sleepTimerEndsAt}
                onClick={() => {
                  clearSleepTimer({ notify: true });
                  setIsSleepOpen(false);
                }}
              >
                Turn Off Timer
              </button>
            </div>
          </article>
        </div>
      ) : null}

      {isRelatedOpen ? (
        <div className="queue-modal-overlay" onClick={() => setIsRelatedOpen(false)}>
          <article
            className="queue-modal glass-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Related tracks"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="queue-modal-head">
              <h3>Related Tracks</h3>
              <button
                type="button"
                className="queue-modal-close"
                aria-label="Close related tracks"
                onClick={() => setIsRelatedOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <p className="queue-modal-subtitle">
              {isLoadingRelated
                ? "Loading related tracks..."
                : `${relatedTracks.length} related track${relatedTracks.length === 1 ? "" : "s"}`}
            </p>

            {isLoadingRelated ? (
              <div className="queue-list">
                {new Array(6).fill(null).map((_, index) => (
                  <article key={`related-skeleton-${index}`} className="queue-item card-skeleton" />
                ))}
              </div>
            ) : relatedTracks.length ? (
              <div className="queue-list">
                {relatedTracks.map((item, index) => (
                  <button
                    key={`${item.id}-${index}`}
                    type="button"
                    className={`queue-item${item.isUnavailable ? " is-unavailable" : ""}`}
                    onClick={() => {
                      playTrack(item, relatedTracks, index);
                      setIsRelatedOpen(false);
                    }}
                    disabled={item.isUnavailable}
                  >
                    <img src={item.image} alt={item.title} />
                    <div className="queue-item-content">
                      <p className="queue-item-title">{item.title}</p>
                      <p className={`queue-item-artist${item.isUnavailable ? " is-warning" : ""}`}>
                        {item.isUnavailable
                          ? item.unavailableText || "Not available in your country"
                          : item.artist}
                      </p>
                    </div>
                    <span className="queue-item-time">{item.duration || "--:--"}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="home-state-inline">No related tracks found for this song.</p>
            )}
          </article>
        </div>
      ) : null}
    </>
  );
}

export default PlayerBar;
